import { isEqual } from 'lodash';
import { useEffect, useRef, useState } from 'react';

import type { Organ } from '../../Organ';
import type {
  ActiveCase,
  Hospital,
  MaoTrip,
  NewCase,
  PathLabel,
} from '../../types';

interface MinimalTrip {
  Legs: Pick<MaoTrip, 'Legs'>[];
}

type MinimalPathLabel = Pick<PathLabel, 'id' | 'name'>;

type UpdatedFieldsMap = Record<string, string[]>;

export const useDetectUpdatedCaseFields = <
  T extends Omit<NewCase, 'oscStatus'>,
>(
  cases: T[],
  options?: {
    skipFields?: (keyof T)[];
    exactFieldsOnly?: (keyof T)[];
  },
): {
  updatedCases: Record<string, string[]>;
} => {
  const [updatedCases, setUpdatedCases] = useState<UpdatedFieldsMap>({});
  const prevCasesRef = useRef<Map<string, T>>(new Map());
  const lastChangeRef = useRef<UpdatedFieldsMap>({});

  useEffect(() => {
    const { skipFields = [], exactFieldsOnly } = options || {};
    const changes: UpdatedFieldsMap = {};

    for (const currentCase of cases) {
      const prevCase = prevCasesRef.current.get(currentCase.id);
      if (!prevCase) {
        continue;
      }

      const keysToCompare = exactFieldsOnly
        ? exactFieldsOnly.filter(key => !skipFields.includes(key))
        : (Object.keys(currentCase) as (keyof T)[]).filter(
            key => !skipFields.includes(key),
          );

      const changedKeys: string[] = [];

      for (const key of keysToCompare) {
        const prev = prevCase[key];
        const curr = currentCase[key];

        if (key === 'organ') {
          const prevName = (prev as Organ)?.name;
          const currName = (curr as Organ)?.name;
          if (prevName !== currName) {
            changedKeys.push('organ');
          }
          continue;
        }

        if (key === 'donorHospital') {
          const prevAddr = (prev as Hospital | null)?.address;
          const currAddr = (curr as Hospital | null)?.address;
          if (prevAddr !== currAddr) {
            changedKeys.push('donorHospital');
          }
          continue;
        }

        if (
          key === 'pathLabels' &&
          Array.isArray(prev) &&
          Array.isArray(curr)
        ) {
          const toMap = (arr: PathLabel[]) =>
            new Map(arr.map(p => [p.name.trim(), true]));
          const prevMap = toMap(prev);
          const currMap = toMap(curr);
          const changed = new Set<string>();
          for (const name of currMap.keys()) {
            if (!prevMap.has(name)) {
              changed.add(`pathLabels:${name}`);
            }
          }
          for (const name of prevMap.keys()) {
            if (!currMap.has(name)) {
              changed.add(`pathLabels:${name}`);
            }
          }
          if (changed.size > 0) {
            changedKeys.push(...changed);
          }
          continue;
        }

        if (key === 'trips' && Array.isArray(prev) && Array.isArray(curr)) {
          const extractRegs = (trips: MaoTrip[] | MinimalTrip[]): string[] =>
            trips.flatMap(trip =>
              (trip.Legs || []).flatMap(leg =>
                leg.Movement?.Aircraft?.Registration
                  ? [leg.Movement.Aircraft.Registration]
                  : [],
              ),
            );

          const prevSet = new Set(extractRegs(prev as MaoTrip[]));
          const currSet = new Set(extractRegs(curr as MaoTrip[]));

          const changedRegs = new Set<string>();
          for (const reg of currSet) {
            if (!prevSet.has(reg)) {
              changedRegs.add(reg);
            }
          }
          for (const reg of prevSet) {
            if (!currSet.has(reg)) {
              changedRegs.add(reg);
            }
          }
          if (changedRegs.size > 0) {
            changedRegs.forEach(reg => changedKeys.push(`trips:${reg}`));
          }
          continue;
        }

        if (!isEqual(prev, curr)) {
          changedKeys.push(key as string);
        }
      }

      if (changedKeys.length > 0) {
        changes[currentCase.id] = changedKeys;
      }
    }

    if (!isEqual(changes, lastChangeRef.current)) {
      setTimeout(() => {
        setUpdatedCases(changes);
        lastChangeRef.current = changes;
      }, 0);
    }

    const newMap = new Map<string, T>();

    for (const c of cases) {
      const cloned = { ...c };

      if ('pathLabels' in c && Array.isArray(c.pathLabels)) {
        (cloned as Partial<{ pathLabels: MinimalPathLabel[] }>).pathLabels = [
          ...c.pathLabels,
        ]
          .map(label => ({
            id: String(label.id),
            name: label.name,
          }))
          .sort((a, b) => a.id.localeCompare(b.id));
      }

      if ('trips' in c && Array.isArray(c.trips)) {
        (cloned as Partial<ActiveCase>).trips =
          c.trips?.map(trip => {
            const legs = Array.isArray(trip.Legs)
              ? trip.Legs.map(leg => ({
                  Movement: {
                    Aircraft: {
                      Registration:
                        leg.Movement?.Aircraft?.Registration ?? null,
                    },
                  },
                }))
              : [];

            return {
              ...trip,
              Legs: legs,
            };
          }) || ([] as ActiveCase['trips']);
      }

      if ('organ' in c && c.organ && 'name' in c.organ) {
        (cloned as Partial<NewCase>).organ = {
          name: c.organ.name,
        } as Organ;
      }

      newMap.set(c.id, cloned);
    }
    prevCasesRef.current = newMap;
  }, [cases, options]);

  return { updatedCases };
};
